import { useEffect, useRef } from "react";

/**
 * Keeps the device screen awake while the component is mounted.
 * Uses the Screen Wake Lock API (Chrome 84+, Safari 16.4+, Firefox 126+).
 * Falls back silently on unsupported browsers.
 *
 * Automatically re-acquires the lock when the tab becomes visible again
 * (the OS releases the lock when the tab goes background).
 */
export function useWakeLock(enabled = true) {
  const wakeLockRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) {
      return;
    }

    let cancelled = false;

    const acquire = async () => {
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          lock.release().catch(() => {});
          return;
        }
        wakeLockRef.current = lock;
        lock.addEventListener("release", () => {
          // OS released it — clear our ref so we re-acquire on visibility change
          wakeLockRef.current = null;
        });
      } catch {
        // User denied, low battery, etc — silently ignore
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && wakeLockRef.current === null) {
        acquire();
      }
    };

    acquire();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [enabled]);
}
